import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { api } from "../api.js";
import { setContentLang, useT } from "../lib/i18n.jsx";
import { lowPower } from "../lib/lowPower.js";
import FloorSelector from "./FloorSelector.jsx";
import IdleReset from "./IdleReset.jsx";
import LangSelector from "./LangSelector.jsx";
import MapCanvas from "./MapCanvas.jsx";
import MapControls from "./MapControls.jsx";
import PoiPanel from "./PoiPanel.jsx";
import AssistantButton from "./AssistantButton.jsx";
import QrButton, { QrGlyph } from "./QrButton.jsx";
import RouteBanner from "./RouteBanner.jsx";
import { viewerLink } from "./lib/deepLink.js";
import { homeCategories } from "./lib/homeCategories.js";
import {
  clearRoute,
  dockedRouteCamera,
  floatingRouteCamera,
  routeBounds,
  routeFence,
  routeLookFor,
  showRoute,
} from "./lib/routeAnim.js";
import { useCompact, usePortrait, useSideBySide, useTopDock } from "./lib/useCompact.js";

// The QR modal -- and with it the `qrcode` library -- is not in the main chunk
// (item 0324). Normal mode prefetches it once the map is up, so the tap that
// opens it has nothing to wait for; low-power mode (item 0315) fetches it only
// when someone actually asks for a QR.
const loadQrModal = () => import("./QrModal.jsx");
const QrModal = lazy(loadQrModal);
// The AI assistant panel (item 0325): only for venues that switched it on, and
// only fetched when someone opens it.
const AssistantPanel = lazy(() => import("./AssistantPanel.jsx"));

// How long the top-docked panel must keep one height before a route is framed
// against it (item 0373). The fold from POI card to directions is two or three
// renders and a ResizeObserver callback -- tens of ms; this only has to
// outlast that, and stay short enough not to read as a delay.
const DOCK_SETTLE_MS = 150;

// Floating layout of a wide portrait screen (item 0377): what the route frame
// keeps clear of besides the measured card and directions panel. right: the
// zoom / compass / floor-pill column (right-3 + w-11, + 12px); bottom: the
// language picker's top edge (bottom-20 + ~40px), which is above Reset view
// and the "Ask me" / QR row, + 12px; gap: between a panel and the frame.
const FLOAT_ROUTE_MARGIN = { top: 24, left: 24, right: 68, bottom: 132, gap: 16 };

export default function ViewerPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const { t, lang } = useT();

  const [venue, setVenue] = useState(null);
  const [featuresFC, setFeaturesFC] = useState(null);
  const [pois, setPois] = useState([]);
  const [error, setError] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [map, setMap] = useState(null);

  const [activeFloorId, setActiveFloorId] = useState(null);
  const [view, setView] = useState({ type: "home" });
  const [route, setRoute] = useState(null);
  // The open QR modal: null, { kind: "continue" } (the bottom-right button,
  // item 0324) or { kind: "share", poi } (a POI card's Share).
  const [qr, setQr] = useState(null);
  const [assistantOpen, setAssistantOpen] = useState(false);

  // Phone-shaped viewport: the panel becomes a dismissable bottom sheet and the
  // map is what you see (item 0093).
  const compact = useCompact();
  // Is there room for the POI card and the directions panel at once? (item 0151)
  const sideBySide = useSideBySide();
  const [panelOpen, setPanelOpen] = useState(true);

  // ?ui=0 — chrome-less embed for thumbnails/previews: just the map.
  const bare = params.get("ui") === "0";

  // Portrait up to 819px wide, or any screen 640px wide or less: the menu docks
  // at the TOP and the map is below it (item 0373). A wider portrait screen --
  // the 1080px Port Premia totem -- keeps the floating card (f0e189a). See
  // useTopDock for what stays as it was (landscape).
  const docked = useTopDock() && !bare;
  const portrait = usePortrait();
  // A wide portrait screen with the floating card (item 0377): the route is
  // framed here, against the card's and the directions panel's MEASURED rects,
  // as the docked case is framed against the dock. Landscape keeps showRoute's
  // own fitBounds, unchanged. Either way showRoute leaves the camera to us.
  const floatFit = !bare && !docked && !compact && sideBySide && portrait;
  const routeFramed = docked || floatFit;

  // How the wayfinding line looks. Normally venue.settings.route_style /
  // .route_color / .route_occluded, all absent on every venue that has not
  // opted in — which is what keeps the default identical to what it has always
  // been.
  // ?route_style= / ?route_color= / ?route_occluded= override them for one page
  // load so the admin (and QA) can see a choice on the real map before saving
  // it to the venue. Read-only and purely cosmetic: an unknown id falls back to
  // the default.
  const look = useMemo(() => {
    const style = params.get("route_style");
    const raw = params.get("route_color");
    const color = raw ? (raw.startsWith("#") ? raw : `#${raw}`) : null;
    // Absent stays NULL rather than false: the venue's own setting has to win
    // when the URL says nothing, and "0" has to be able to preview OFF.
    const occ = params.get("route_occluded");
    const occluded = occ == null ? null : occ === "1" || occ === "true";
    const override = style || color || occ != null ? { style, color, occluded } : null;
    return routeLookFor(venue?.settings, override);
  }, [venue, params]);

  // Is the viewer genuinely RUNNING on a totem? That is the ?kiosk= parameter
  // being present -- NOT the `kiosk` object below, which falls back to the
  // venue's first totem for demo links and the admin's "Ver mapa" and is
  // therefore set almost always.
  const onTotem = !!params.get("kiosk");

  const kiosk = useMemo(() => {
    if (!venue) return null;
    // ?from= is a phone that scanned a totem's QR (item 0324): it names the
    // totem the visitor was standing at, as the route origin, WITHOUT turning
    // the phone into a totem the way ?kiosk= would.
    const wanted = params.get("kiosk") || params.get("from");
    if (wanted) return venue.kiosks.find((k) => k.slug === wanted) ?? null;
    // No ?kiosk= (demo link, admin "Ver mapa"): default to the venue's first
    // totem so "you are here" + routing still work instead of silently hiding.
    return venue.kiosks[0] ?? null;
  }, [venue, params]);

  // The frame the map opens on, and the one "reset view" returns to.
  // A totem is oriented by the wall it hangs on, so there the totem's heading
  // outranks the saved bearing. Everywhere else -- a phone, a demo link, the
  // admin's own preview -- the visitor gets the rotation the admin composed
  // and saved. Keying this off the `kiosk` object instead of ?kiosk= is what
  // silently threw the saved bearing away for every visitor (item 0096).
  const homeCamera = useMemo(() => {
    if (!venue) return null;
    const s = venue.settings ?? {};
    return {
      center: s.center,
      zoom: s.zoom ?? 17,
      bearing: (onTotem ? kiosk?.heading : null) ?? s.bearing ?? 0,
      pitch: s.pitch ?? 50,
    };
  }, [venue, kiosk, onTotem]);

  // Boot: bundle + features + all POIs in parallel.
  useEffect(() => {
    let cancelled = false;
    Promise.all([api.venue(slug), api.features(slug), api.pois(slug)])
      .then(([bundle, fc, poiRows]) => {
        if (cancelled) return;
        // Before any name renders: pickName() falls back to it.
        setContentLang(bundle?.settings?.default_lang);
        setVenue(bundle);
        setFeaturesFC(fc);
        setPois(poiRows);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e?.code === "maps3d_not_entitled") setBlocked(true);
        else setError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Browser-tab title = the map's name.
  useEffect(() => {
    if (venue?.name) document.title = venue.name;
    return () => {
      document.title = "promomaps";
    };
  }, [venue?.name]);

  // Default floor: the kiosk's floor, else the lowest level.
  useEffect(() => {
    if (!venue || activeFloorId !== null) return;
    const fallback = [...venue.floors].sort((a, b) => a.level - b.level)[0];
    setActiveFloorId(kiosk?.floor_id ?? fallback?.id ?? null);
  }, [venue, kiosk, activeFloorId]);

  const openPoi = useCallback(
    (poiSlug, from) => {
      api
        .poi(slug, poiSlug)
        .then((poi) => {
          setView({ type: "poi", poi, from });
          setActiveFloorId(poi.floor_id);
        })
        .catch(() => {});
    },
    [slug],
  );

  const handleShowRoute = useCallback(
    async (poi) => {
      if (!kiosk || !map) return;
      try {
        const result = await api.route(slug, { fromKiosk: kiosk.slug, toPoi: poi.slug });
        setRoute({ ...result, poi });
        const first = result.segments[0];
        if (first) setActiveFloorId(first.floor_id);
        showRoute(map, result, first?.floor_id ?? null, { compact, docked, framed: floatFit, look });
      } catch (e) {
        setError(e.message);
        setTimeout(() => setError(null), 4000);
      }
    },
    [kiosk, map, slug, compact, docked, floatFit, look],
  );

  // ?to= deep link (a phone that scanned a QR): open the POI, and with
  // ?route=1 draw the directions from ?from= as well (item 0324), so the phone
  // continues exactly where the totem was. Once per page load. A route needs
  // the map, so that case waits for it.
  const deepLinkDone = useRef(false);
  useEffect(() => {
    const to = params.get("to");
    if (!venue || !to || deepLinkDone.current) return;
    const wantRoute = params.get("route") === "1" && !!kiosk;
    if (wantRoute && !map) return;
    deepLinkDone.current = true;
    api
      .poi(slug, to)
      .then((poi) => {
        setView({ type: "poi", poi });
        setActiveFloorId(poi.floor_id);
        // On a phone the menu starts folded away; a visitor who scanned a QR
        // for THIS place should see its card, not have to find it.
        setPanelOpen(true);
        if (wantRoute) handleShowRoute(poi);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venue, map, kiosk]);

  const handleCancelRoute = useCallback(() => {
    setRoute(null);
    if (map) clearRoute(map);
  }, [map]);

  // Tapping an instruction in the step list (item 0110) shows the visitor where
  // that turn is instead of leaving them to find it on the line. It switches
  // floor first when the step is on another one, which is the case the list
  // exists for.
  const handleFocusStep = useCallback(
    (step) => {
      if (step?.floorId != null) setActiveFloorId(step.floorId);
      if (map && Array.isArray(step?.at) && step.at.length === 2) {
        map.easeTo({
          center: step.at,
          zoom: Math.max(map.getZoom(), 18.5),
          duration: 700,
        });
      }
    },
    [map],
  );

  // Kiosk inactivity: wipe everything back to the home state for the next
  // visitor. Only armed when the viewer runs on a totem (?kiosk=).
  const handleIdle = useCallback(() => {
    setView({ type: "home" });
    setRoute(null);
    setQr(null);
    setAssistantOpen(false);
    if (map) {
      clearRoute(map);
      if (kiosk) setActiveFloorId(kiosk.floor_id);
      if (homeCamera) map.easeTo({ ...homeCamera, duration: 1200 });
    }
  }, [map, kiosk, homeCamera]);

  // Only a phone may collapse the menu (item 0314). Above the phone breakpoint
  // -- useCompact's "(max-width: 640px), (max-height: 520px)" -- the panel is
  // ALWAYS shown: no X, and nothing else (route, idle reset, resize) folds it
  // to the logo button. That button is 56px; on a 55" totem it is a speck, and
  // visitors walking up to a screen with only a map on it never find the menu
  // ("For Big totems can be small and not intuitive"). This used to key off
  // ?kiosk= alone, so a totem playing the map WITHOUT that parameter -- web-kiosk
  // 3D Map mode, a demo link on a big screen -- got the X and lost the menu.
  //
  // A ?kiosk= totem never collapses even when it is phone-shaped: nobody taps a
  // logo on a wall to discover a menu exists. On a phone it starts collapsed --
  // the visitor scanned a QR to see the MAP.
  const collapsible = compact && !onTotem && !bare;
  useEffect(() => {
    // Crossing up from phone to totem width re-opens it; crossing down gives
    // the phone its usual collapsed start.
    setPanelOpen(!collapsible);
  }, [compact, collapsible]);
  const menuShown = !collapsible || panelOpen;

  // Asking for directions makes the route the primary interface. On a phone the
  // menu gets out of its way (item 0091 + 0093); the logo reopens it, and picking
  // another POI re-routes instead of being refused.
  //
  // Above phone width the menu no longer yields (item 0314). Where the card and
  // the directions panel fit side by side (>= 820px) nothing changes. In the
  // band between 641px and 819px they do not (item 0151), so they STACK: the
  // card keeps the top-left, capped to the top half, and the directions panel
  // docks below it -- see `stacked` on PoiPanel and RouteBanner.
  useEffect(() => {
    if (route && collapsible) setPanelOpen(false);
  }, [route, collapsible]);
  // Top-docked (item 0373), the menu and directions ALWAYS share one column,
  // at the top, at any width -- so the 641-819px band's own stacking is only
  // for landscape.
  const stacked = !compact && !sideBySide && !!route && !docked;
  const dockRoute = docked && !!route;

  // In that band there is not room for a full menu card AND the directions
  // (QA on 751d359: at 800x600 a fixed 50/50 split clipped the "Take the
  // elevator to P1" button in half and hid the step list). So while a route
  // is shown there, the card shrinks to a full-width bar -- venue logo and
  // name, 340px wide, never the 56px burger -- and the directions get the
  // height. Tapping the bar brings the whole menu back; opening the step list
  // or asking for a new route hands the space back to the directions.
  const [menuExpanded, setMenuExpanded] = useState(false);
  useEffect(() => setMenuExpanded(false), [route]);
  // Docked on a totem with a route, the menu folds to its search bar the same
  // way (on a phone it is `panelOpen` that folds it, as it always was).
  const menuPeek = (stacked || (dockRoute && !collapsible)) && !menuExpanded;
  const panelExpanded = menuShown && !menuPeek;

  // Docked: what folds the menu, and what brings it back for a POI the visitor
  // tapped ON THE MAP -- its card is at the top now, so it has to be open to
  // be seen (a folded phone menu used to leave the tap with no visible effect).
  const foldMenu = useCallback(() => {
    if (collapsible) setPanelOpen(false);
    else setMenuExpanded(false);
  }, [collapsible]);
  const revealMenu = useCallback(() => {
    if (!docked) return;
    if (collapsible) setPanelOpen(true);
    else setMenuExpanded(true);
  }, [docked, collapsible]);

  const selectedPoi = view.type === "poi" ? view.poi : null;

  // ---- Top dock geometry (item 0373) ----------------------------------------
  //
  // How far up from the bottom edge the menu column must stop, so it never
  // reaches the controls below it. Measured from the controls' own classes:
  //   phone  -- right column at bottom-10: zoom 80 + compass 40 + reset 40 +
  //             2 gaps, plus 48 per floating button (assistant, QR) while the
  //             menu is folded; floor pills at bottom-16 on the left, 50 each.
  //   totem  -- compass top edge at bottom-48 + 40 = 232px; floor pills from
  //             bottom-64 (256px) up, 50 each.
  const floorCount = venue && venue.floors.length >= 2 ? venue.floors.length : 0;
  const floatingButtons = compact && !menuShown ? 1 + (venue?.assistant ? 1 : 0) : 0;
  const dockReserve = compact
    ? Math.max(40 + 176 + floatingButtons * 48, floorCount ? 64 + floorCount * 50 : 56) + 8
    : Math.max(232, floorCount ? 256 + floorCount * 50 : 0) + 12;

  // Camera padding = the bottom edge of what the column actually shows (menu,
  // or folded bar, plus directions), measured live. MapLibre then centres
  // everything -- the opening frame with "You are here", the selected POI,
  // Reset view, the idle reset -- in the map left visible below it.
  const [dockEl, setDockEl] = useState(null);
  const [dockBottom, setDockBottom] = useState(0);
  useEffect(() => {
    if (!dockEl) {
      setDockBottom(0);
      return undefined;
    }
    const measure = () => {
      const top = dockEl.parentElement.getBoundingClientRect().top;
      let bottom = 0;
      for (const child of dockEl.children) {
        const r = child.getBoundingClientRect();
        if (r.height > 0) bottom = Math.max(bottom, r.bottom - top);
      }
      setDockBottom(Math.round(bottom));
    };
    const ro = new ResizeObserver(measure);
    const watch = () => {
      ro.disconnect();
      ro.observe(dockEl);
      for (const child of dockEl.children) ro.observe(child);
    };
    // The directions panel comes and goes as a child of the column.
    const mo = new MutationObserver(() => {
      watch();
      measure();
    });
    mo.observe(dockEl, { childList: true });
    watch();
    measure();
    return () => {
      ro.disconnect();
      mo.disconnect();
    };
  }, [dockEl]);

  // Docked with a route (QA FAIL on 041120c): the route is framed against the
  // panel's FINAL height. "Show route" is tapped on the expanded POI card,
  // which only then folds to the search bar + directions; fitting at that
  // moment framed the route for a panel that was about to shrink, and the
  // padding ease that followed only slid it, never re-framed it. So the fit
  // waits until the dock has stopped changing size (DOCK_SETTLE_MS after the
  // last measurement), then sets the padding and the camera in ONE ease.
  //
  // Later size changes (the step list opened or closed) re-frame the route
  // the same way -- unless the visitor has moved the map since, in which case
  // their view is theirs and only the padding follows.
  //
  // The floating layout of a wide portrait screen (floatFit, item 0377) is
  // framed by the same rules, against the floating panels -- see below.
  const routeFit = useRef({ route: null, pending: false, touched: false });
  useEffect(() => {
    routeFit.current = {
      route: routeFramed ? route : null,
      pending: routeFramed && !!route,
      touched: false,
    };
  }, [route, routeFramed]);
  useEffect(() => {
    if (!map) return undefined;
    // Any camera move this page did not tag as its own is the visitor's (or a
    // step / reset / zoom button they pressed).
    const onMoveStart = (e) => {
      if (!e.rdsCamera) routeFit.current.touched = true;
    };
    map.on("movestart", onMoveStart);
    return () => map.off("movestart", onMoveStart);
  }, [map]);
  // While the route is shown the venue fence follows it (routeFence); when it
  // goes, the stock fence comes back and the camera glides inside it -- unless
  // the camera is already on its way somewhere (the idle reset's ease home):
  // an ease here would cancel that one and leave the totem on the old route.
  useEffect(() => {
    if (!map || !routeFramed || !route) return undefined;
    return () => {
      map.setTransformConstrain(null);
      if (!map.isMoving()) {
        map.easeTo({ center: map.getCenter(), duration: 400 }, { rdsCamera: true });
      }
    };
  }, [map, routeFramed, route]);

  const paddedOnce = useRef(false);
  useEffect(() => {
    if (!map) return undefined;
    const h = map.getContainer().clientHeight;
    // Never more than the canvas minus a strip MapLibre can still centre in.
    const top = docked && dockBottom ? Math.min(dockBottom + 8, Math.max(0, h - 120)) : 0;
    const padding = { top, bottom: 0, left: 0, right: 0 };

    const fit = routeFit.current;
    const bounds = docked && route && fit.route === route ? routeBounds(route) : null;
    if (bounds && (fit.pending || !fit.touched)) {
      const timer = setTimeout(() => {
        // Clear of the chrome over the map. Phone: the zoom/compass column on
        // the right, the language pill bottom-left. Totem: everything sits
        // along the bottom, `dockReserve` tall.
        const margin = compact
          ? { top: 12, bottom: 68, left: 12, right: 60 }
          : { top: 24, bottom: dockReserve, left: 24, right: 24 };
        const cam = dockedRouteCamera(map, route, top, margin);
        if (!cam) return;
        const fence = routeFence(map, bounds, cam.center);
        if (fence) map.setTransformConstrain(fence);
        map.easeTo({ ...cam, padding, duration: fit.pending ? 900 : 350 }, { rdsCamera: true });
        fit.pending = false;
        paddedOnce.current = true;
      }, DOCK_SETTLE_MS);
      return () => clearTimeout(timer);
    }

    const cur = map.getPadding();
    if (Math.abs((cur.top ?? 0) - top) < 2 && !cur.bottom && !cur.left && !cur.right) return undefined;
    // The first one is the opening frame: no slide. After that, a short ease,
    // so a panel folding away visibly hands its room back to the map.
    const duration = paddedOnce.current ? 350 : 0;
    paddedOnce.current = true;
    const apply = () => map.easeTo({ padding, duration }, { rdsCamera: true });
    // A padding-only ease would CANCEL a camera move in flight -- the ease to
    // a POI just tapped -- because MapLibre runs one camera animation at a
    // time. Let it land, then shift what it centred into the visible area (an
    // ease with only `padding` keeps the same point centred).
    if (map.isMoving()) {
      map.once("moveend", apply);
      return () => map.off("moveend", apply);
    }
    apply();
    return undefined;
    // dockReserve and compact only shape the frame of a fit already pending.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, docked, dockBottom, route]);

  // ---- Floating route frame (item 0377) -------------------------------------
  //
  // A wide portrait screen keeps the floating card top-left and the directions
  // panel top-right (f0e189a). The route is framed in the free map around
  // them, measured live like the dock: their rects, relative to the map, from
  // a ResizeObserver on each and a MutationObserver for the directions panel
  // coming and going. The fit waits until they have kept one size for
  // DOCK_SETTLE_MS, so it frames the panels' final shape, never a transient.
  const [floatEl, setFloatEl] = useState(null);
  const [floatRects, setFloatRects] = useState(null);
  useEffect(() => {
    if (!floatEl || !floatFit) {
      setFloatRects(null);
      return undefined;
    }
    const measure = () => {
      const root = floatEl.parentElement.getBoundingClientRect();
      const rects = [];
      for (const child of floatEl.children) {
        const r = child.getBoundingClientRect();
        if (r.width <= 0 || r.height <= 0) continue;
        rects.push({
          left: Math.round(r.left - root.left),
          top: Math.round(r.top - root.top),
          right: Math.round(r.right - root.left),
          bottom: Math.round(r.bottom - root.top),
        });
      }
      setFloatRects((prev) => (JSON.stringify(prev) === JSON.stringify(rects) ? prev : rects));
    };
    const ro = new ResizeObserver(measure);
    const watch = () => {
      ro.disconnect();
      for (const child of floatEl.children) ro.observe(child);
    };
    const mo = new MutationObserver(() => {
      watch();
      measure();
    });
    mo.observe(floatEl, { childList: true });
    watch();
    measure();
    return () => {
      ro.disconnect();
      mo.disconnect();
    };
  }, [floatEl, floatFit]);

  useEffect(() => {
    if (!map || !floatFit || !route || !floatRects) return undefined;
    const fit = routeFit.current;
    if (fit.route !== route || !(fit.pending || !fit.touched)) return undefined;
    const bounds = routeBounds(route);
    if (!bounds) return undefined;
    const timer = setTimeout(() => {
      const best = floatingRouteCamera(map, route, floatRects, FLOAT_ROUTE_MARGIN);
      if (!best) return;
      const fence = routeFence(map, bounds, best.cam.center);
      if (fence) map.setTransformConstrain(fence);
      map.easeTo({ ...best.cam, duration: fit.pending ? 900 : 350 }, { rdsCamera: true });
      fit.pending = false;
    }, DOCK_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [map, floatFit, floatRects, route]);

  // "Sin audio" (item 0323): a screen without speakers. A totem (?kiosk=)
  // uses its own setting when it has one, else the venue's; phones and other
  // web visitors -- including ?from= links, whose kiosk is only a route
  // origin -- follow the venue's. On: every POI video muted, no sound button.
  const noAudio =
    onTotem && typeof kiosk?.no_audio === "boolean"
      ? kiosk.no_audio
      : venue?.settings?.no_audio === true;

  // Normal mode: fetch the QR chunk while nobody is looking (see loadQrModal).
  useEffect(() => {
    if (!map || bare || lowPower().on) return undefined;
    const idle = window.requestIdleCallback ?? ((fn) => setTimeout(fn, 3000));
    const cancel = window.cancelIdleCallback ?? clearTimeout;
    const handle = idle(() => loadQrModal().catch(() => {}), { timeout: 8000 });
    return () => cancel(handle);
  }, [map, bare]);

  // The link the open QR carries: the current state, from an allowlist
  // (lib/deepLink.js). "continue" takes the POI on screen and, if directions
  // are showing, the route; "share" takes the POI whose card was shared.
  const qrUrl = useMemo(() => {
    if (!qr || !venue) return null;
    const poi = qr.kind === "share" ? qr.poi : (route?.poi ?? selectedPoi);
    return viewerLink({
      venueSlug: venue.slug,
      lang,
      poiSlug: poi?.slug,
      fromKiosk: kiosk?.slug,
      route: !!route && route.poi?.slug === poi?.slug,
    });
  }, [qr, venue, lang, route, selectedPoi, kiosk]);
  const closeQr = useCallback(() => setQr(null), []);

  // The panel's grid hides "Favorites" while no published POI is a favorite
  // (item 0389). Only the panel gets the trimmed list; the map keeps them all.
  const panelVenue = useMemo(
    () => venue && { ...venue, categories: homeCategories(venue.categories, pois) },
    [venue, pois],
  );

  // Phone QR button (item 0324). Floating in the left control column while the
  // menu is folded away; while the bottom sheet is open it moves INTO the
  // sheet's header (PoiPanel `headerExtras`), because on a 375x667 phone the
  // column reaches the sheet's title and search box (QA on 63d8807).
  const phoneQrButton = (
    <button
      data-qr-button
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-800
                 text-white shadow-md"
      onClick={() => setQr({ kind: "continue" })}
      aria-label={t("qr_button")}
      title={t("qr_button")}
    >
      <QrGlyph size={20} />
    </button>
  );

  // The 90 s idle reset is for a SCREEN in a public place, not for a visitor's
  // own phone (QA on 63d8807: a phone opened from the QR lost its route and
  // card after 90 s in a pocket). Armed on:
  //   - a real totem, ?kiosk=;
  //   - a totem-sized screen with no ?kiosk= (web-kiosk 3D Map mode runs like
  //     that today and relies on the reset).
  // Never on a ?from= link (a phone that scanned a totem's QR), and never on a
  // phone-shaped viewport without ?kiosk=.
  const idleArmed =
    !bare && !!kiosk && (onTotem || (!params.get("from") && !compact));

  // Assistant (item 0325). A card's name opens the place; "Cómo llegar" opens
  // it AND starts the normal route from this totem. Either way the panel gets
  // out of the way of what the visitor asked to see.
  const assistantConfig = venue?.assistant ?? null;
  const assistantOpenPoi = useCallback(
    (poiSlug) => {
      setAssistantOpen(false);
      openPoi(poiSlug);
      if (compact && collapsible) setPanelOpen(true);
    },
    [openPoi, compact, collapsible],
  );
  const assistantRoute = useCallback(
    (poiSlug) => {
      setAssistantOpen(false);
      api
        .poi(slug, poiSlug)
        .then((poi) => {
          setView({ type: "poi", poi });
          setActiveFloorId(poi.floor_id);
          handleShowRoute(poi);
        })
        .catch(() => {});
    },
    [slug, handleShowRoute],
  );

  // Subscription lapsed: show a branded notice rather than a broken map, so
  // whoever walks past the screen knows what to do.
  if (blocked) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 bg-slate-900 p-8 text-center">
        <svg viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="1.5" width="56" height="56">
          <path d="M9 20l-5.447-2.724A1 1 0 0 1 3 16.382V5.618a1 1 0 0 1 1.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0 0 21 18.382V7.618a1 1 0 0 0-.553-.894L15 4m0 13V4m0 0L9 7" />
        </svg>
        <div className="text-xl font-semibold text-white">{t("err_maps3d_title")}</div>
        <div className="max-w-md text-sm leading-relaxed text-slate-300">{t("err_maps3d_body")}</div>
        <div className="mt-2 text-xs uppercase tracking-widest text-slate-500">Digital Signage RDS</div>
      </div>
    );
  }
  if (error && !venue) {
    return (
      <div className="flex h-full items-center justify-center bg-slate-100 p-6 text-center text-slate-600">
        {error}
      </div>
    );
  }
  if (!venue || !featuresFC) {
    return (
      <div className="flex h-full items-center justify-center bg-slate-100 text-slate-500">
        {t("loading")}
      </div>
    );
  }

  return (
    <div className="kiosk relative h-full overflow-hidden">
      {/* The map is its own stacking context (item 0373): anything inside it
          that sets a z-index -- DOM POI markers rank themselves by z-index --
          stays BELOW the menu column (z-20) and the controls (z-10) instead
          of painting over the top-docked menu. */}
      <div style={{ position: "absolute", inset: 0, isolation: "isolate" }}>
      <MapCanvas
        venue={venue}
        featuresFC={featuresFC}
        pois={pois}
        lang={lang}
        activeFloorId={activeFloorId}
        selectedPoi={selectedPoi}
        kiosk={kiosk}
        accent={look.color}
        camera={homeCamera}
        onSelectPoi={(poiSlug) => {
          openPoi(poiSlug);
          revealMenu();
        }}
        onMapReady={setMap}
      />
      </div>

      {(() => {
        if (bare) return null;
        const panelEl = (
          <PoiPanel
            venue={panelVenue}
            compact={compact}
            collapsible={collapsible}
            open={menuShown}
            stacked={stacked}
            peek={menuPeek}
            onExpand={() => setMenuExpanded(true)}
            onOpen={() => setPanelOpen(true)}
            onCollapse={() => setPanelOpen(false)}
            docked={docked}
            foldable={collapsible || dockRoute}
            onFold={foldMenu}
            view={view}
            onOpenCategory={(category) => setView({ type: "category", category })}
            onSearch={(q) => setView({ type: "search", q })}
            onOpenPoi={(poiSlug) => {
              const from = view.type !== "poi" ? () => setView(view) : undefined;
              openPoi(poiSlug, from);
              if (compact && collapsible) setPanelOpen(true);
            }}
            onHome={() => setView({ type: "home" })}
            onShowRoute={kiosk ? handleShowRoute : null}
            onShare={(poi) => setQr({ kind: "share", poi })}
            noAudio={noAudio}
            headerExtras={
              compact && menuShown ? (
                <>
                  {/* Same rule for the assistant (item 0325): inside the open
                      sheet, never floating over its title or search box. */}
                  {assistantConfig && (
                    <AssistantButton compact onClick={() => setAssistantOpen(true)} />
                  )}
                  {phoneQrButton}
                </>
              ) : null
            }
            routeActive={!!route}
          />
        );
        const bannerEl = route && (
          <RouteBanner
            route={route}
            venue={venue}
            compact={compact}
            atTop={compact && menuShown}
            stacked={stacked || docked}
            menuPeek={docked ? !panelExpanded : menuPeek}
            onStepsOpened={docked ? foldMenu : () => setMenuExpanded(false)}
            activeFloorId={activeFloorId}
            onFloorSwitch={setActiveFloorId}
            onFocusStep={handleFocusStep}
            onCancel={handleCancelRoute}
          />
        );
        // 641-819px with a route (item 0314): card and directions share ONE
        // left column, in flow, so the directions start at the card's real
        // bottom instead of at a guessed 50/50 split. The column ends 64px
        // above the bottom, where the language picker and Reset view sit in
        // this band. Empty column area passes touches through to the map.
        // Top dock (item 0373): menu (or its folded search bar), then the
        // directions, in one column down from the top edge. It stops
        // `dockReserve` above the bottom, where the controls are; the empty
        // part of it passes touches through to the map.
        if (docked) {
          return (
            <div
              ref={setDockEl}
              data-dock
              className={
                "pointer-events-none absolute z-20 flex flex-col gap-2 " +
                (compact ? "inset-x-2 top-2" : "inset-x-3 top-3")
              }
              style={{ bottom: dockReserve }}
            >
              {panelEl}
              {bannerEl}
            </div>
          );
        }
        if (stacked) {
          return (
            <div className="pointer-events-none absolute bottom-16 left-3 right-[68px] top-3 z-20 flex flex-col gap-3">
              {panelEl}
              {bannerEl}
            </div>
          );
        }
        // Floating (item 0377): the card and the directions panel position
        // themselves; this wrapper generates no box (display: contents) and is
        // only there so their rects can be measured for the route frame.
        return (
          <div ref={setFloatEl} data-float style={{ display: "contents" }}>
            {panelEl}
            {bannerEl}
          </div>
        );
      })()}

      {!bare && (
      <FloorSelector
        floors={venue.floors}
        activeFloorId={activeFloorId}
        onSelect={setActiveFloorId}
        routeFloorIds={route?.segments.map((s) => s.floor_id)}
        compact={compact}
        docked={docked}
      />
      )}
      {!bare && (
        <MapControls map={map} homeCamera={homeCamera} compact={compact} stacked={stacked} docked={docked}>
          {/* Phone: the assistant (item 0325) and QR (item 0324) buttons join
              the left control column. */}
          {compact && !menuShown && assistantConfig && (
            <AssistantButton compact onClick={() => setAssistantOpen(true)} />
          )}
          {compact && !menuShown && phoneQrButton}
        </MapControls>
      )}
      {!bare && !compact && (stacked || !assistantConfig) && (
        // Stacked band (a route in 641-819px): the QR glyph only. The assistant
        // button is left out there -- the visitor is following a route, and the
        // bottom row belongs to the language picker.
        <QrButton stacked={stacked} onClick={() => setQr({ kind: "continue" })} />
      )}
      {!bare && !compact && !stacked && assistantConfig && (
        <div className="absolute bottom-10 right-3 z-20 flex items-center gap-2">
          <AssistantButton onClick={() => setAssistantOpen(true)} />
          <QrButton inline onClick={() => setQr({ kind: "continue" })} />
        </div>
      )}
      {assistantOpen && assistantConfig && (
        <Suspense fallback={null}>
          <AssistantPanel
            venue={venue}
            config={assistantConfig}
            pois={pois}
            compact={compact}
            canRoute={!!kiosk}
            onOpenPoi={assistantOpenPoi}
            onRoute={assistantRoute}
            onClose={() => setAssistantOpen(false)}
          />
        </Suspense>
      )}
      {!bare && <LangSelector compact={compact} stacked={stacked} docked={docked} />}
      {idleArmed && <IdleReset seconds={90} onIdle={handleIdle} />}
      {qr && qrUrl && (
        <Suspense fallback={null}>
          <QrModal
            url={qrUrl}
            title={qr.kind === "continue" ? t("qr_title") : null}
            hint={qr.kind === "continue" ? t("qr_hint") : t("share_hint")}
            onClose={closeQr}
          />
        </Suspense>
      )}

      {error && venue && (
        <div
          className="absolute left-1/2 top-3 z-30 -translate-x-1/2 rounded-lg bg-[#e63946] px-4 py-2 text-sm font-medium text-white shadow-lg"
          // Docked: just under the menu, not on its search field (item 0373).
          style={docked && dockBottom ? { top: dockBottom + 8 } : undefined}
        >
          {error}
        </div>
      )}
    </div>
  );
}
